import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/ReporteCargaAcademicaStyles/ReporteCargaAcademica.module.css';

const PAGE_SIZE = 20;

/**
 * Tabla del Reporte de Carga Académica.
 *
 * Props:
 *  - resultados: Array<row>
 *  - tipoReporte: 'ASESOR' | 'ESTUDIANTE'
 *  - contexto: {ciclo, campus, seccion, asesor, estudiante, matricula}
 */
function TablaCargaAcademica({ resultados = [], tipoReporte = 'ASESOR', contexto = {} }) {
  const [page, setPage] = useState(1);

  const total = resultados.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => { setPage(1); }, [resultados, tipoReporte]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return resultados.slice(start, start + PAGE_SIZE);
  }, [resultados, page]);

  const isAsesor = tipoReporte === 'ASESOR';

  return (
    <>
      <div className={styles.reportHeader}>
        <h2 className={styles.reportTitle}>
          REPORTE DE CARGA ACADÉMICA — {isAsesor ? 'ASESOR' : 'ESTUDIANTE'}
        </h2>
      </div>

      <div className={styles.reportMeta}>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Ciclo:</span>
          <span>{contexto.ciclo || '—'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Campus:</span>
          <span>{contexto.campus || 'Todos'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Sección:</span>
          <span>{contexto.seccion || 'Todas'}</span>
        </div>
        {isAsesor ? (
          <div className={styles.reportMetaItem}>
            <span className={styles.reportMetaLabel}>Asesor:</span>
            <span>{contexto.asesor || 'Todos'}</span>
          </div>
        ) : (
          <>
            <div className={styles.reportMetaItem}>
              <span className={styles.reportMetaLabel}>Estudiante:</span>
              <span>{contexto.estudiante || 'Todos'}</span>
            </div>
            <div className={styles.reportMetaItem}>
              <span className={styles.reportMetaLabel}>Matrícula:</span>
              <span>{contexto.matricula || 'Todas'}</span>
            </div>
          </>
        )}
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Total de grupos:</span>
          <span>{total}</span>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.reportTable}>
          <thead>
            <tr>
              <th className={styles.cellCenter} style={{ width: 60 }}>#</th>
              {isAsesor ? (
                <th>Asesor</th>
              ) : (
                <>
                  <th>Matrícula</th>
                  <th>Estudiante</th>
                </>
              )}
              <th>Clave de Grupo</th>
              <th>Materia</th>
              <th>Turno</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={isAsesor ? 5 : 6} className={styles.cellCenter} style={{ padding: 20, color: '#999' }}>
                  No hay registros para mostrar.
                </td>
              </tr>
            ) : (
              pageRows.map((r, idx) => {
                const consecutivo = (page - 1) * PAGE_SIZE + idx + 1;
                return (
                  <tr key={`${r.id_grupo}-${r.id_estudiante || r.id_asesor || idx}`}>
                    <td className={styles.cellCenter}>{consecutivo}</td>
                    {isAsesor ? (
                      <td>{r.asesor || '—'}</td>
                    ) : (
                      <>
                        <td>{r.matricula || '—'}</td>
                        <td>{r.estudiante || '—'}</td>
                      </>
                    )}
                    <td>{r.clave_grupo || '—'}</td>
                    <td>{r.materia || '—'}</td>
                    <td>{r.turno || '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            Mostrando {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
          </span>
          <div className={styles.pageButtons}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              « Anterior
            </button>
            <span className={styles.pageInfo}>Página {page} de {totalPages}</span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente »
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default TablaCargaAcademica;
